'use client';

import { Icon } from '@asurion-core/mx-asurion-ui-react';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';

interface AccordionProps {
  header: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

const Accordion: React.FC<AccordionProps> = ({
  header,
  children,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState<number | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleResize = () => setWindowWidth(window.innerWidth);
      handleResize();
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  return (
    <div className="border border-0 border-b border-solid border-[#eeedf1] xl:border-none">
      <motion.header initial={false}>
        <div className="accordion-header flex inline-flex h-20 cursor-pointer items-center justify-start gap-x-4 xl:h-10 xl:cursor-default xl:items-center xl:justify-between xl:p-0 xl:pt-4 [&>div>span]:text-sm xl:[&>div>span]:text-xl">
          {header}
          <Icon
            src={isOpen ? 'chevron-up' : 'chevron-down'}
            size="xlarge"
            className="icon justify-end"
            onClick={() => {
              setIsOpen(!isOpen);
            }}
          />
        </div>
      </motion.header>

      <AnimatePresence initial={false}>
        {(isOpen || (windowWidth != null && windowWidth >= 1280)) && (
          <motion.section
            key="content"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            className="w-full"
            variants={{
              open: { opacity: 1, height: 'auto' },
              collapsed: { opacity: 1, height: 'auto' },
            }}
            transition={{ duration: 0.2 }}
          >
            <motion.div
              variants={{ collapsed: { scale: 1 }, open: { scale: 1 } }}
              transition={{ duration: 0.2 }}
            >
              <div className="accordion-body flex flex-col">{children}</div>
            </motion.div>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Accordion;
